import { useRef, useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { iaApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import { DEVISE } from '../../lib/currency';
import { prepareFichiers } from '../../lib/document-ia';
import { messageIa } from '../../lib/ia';
import {
  NOUVEAU_CLIENT,
  brouillonDepuisNotes,
  offreDepuisNotes,
  sansPrix,
} from '../../lib/offre-ia';
import { CLE_PACK_IA, packIaActif } from '../../lib/pack-ia';
import { verifieFichiers } from '../../lib/soumission';
import { C } from '../../theme/palette';

const EN_COURS = 'lecture';

/**
 * « 🤖 Faire une offre avec l'IA » : la feuille de notes scannée devient un
 * brouillon d'offre, que l'utilisateur vérifie avant de le reprendre.
 *
 * Le serveur a corrigé les fautes, reconnu le client parmi ceux de la société
 * et chiffré les lignes depuis le catalogue ; les articles absents arrivent à
 * prix vide. Rien n'est créé avant « Créer l'offre ».
 *
 * @param {object} props propriétés du composant
 * @param {(offre: {client: object|null, titre: string, lignes: object[]}) => void} props.onOffre
 *   reçoit le client choisi (existant ou à créer), l'objet et les lignes
 * @returns {import('react').ReactElement|null} le bouton et l'écran de vérification, ou rien sans le pack
 */
export function OffreIa({ onOffre }) {
  const { reglageJson } = useReglages();
  const actif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const entree = useRef(null);
  const [etat, setEtat] = useState('');
  const [brouillon, setBrouillon] = useState(null);
  if (!actif) return null;

  const lit = async (fichiers) => {
    const erreur = verifieFichiers(fichiers, {
      texteAdmis: false,
      document: 'Une feuille de notes',
    });
    if (erreur) {
      setEtat(erreur);
      return;
    }
    setEtat(EN_COURS);
    try {
      const prets = await prepareFichiers(fichiers);
      setBrouillon(brouillonDepuisNotes(await iaApi.lectureNotes({ fichiers: prets })));
      setEtat('');
    } catch (e) {
      setEtat(e?.status != null ? messageIa(e) : e?.message || messageIa(e));
    }
  };

  const majLigne = (id, champ, valeur) =>
    setBrouillon((b) => ({
      ...b,
      lignes: b.lignes.map((l) => (l.id === id ? { ...l, [champ]: valeur } : l)),
    }));
  const majClient = (champ, valeur) =>
    setBrouillon((b) => ({ ...b, nouveauClient: { ...b.nouveauClient, [champ]: valeur } }));

  const cree = () => {
    const vides = brouillon.lignes.filter(sansPrix).length;
    if (
      vides > 0 &&
      !window.confirm(
        `${vides} ligne(s) n'ont pas de prix : elles partiront à 0. Créer l'offre quand même ?`,
      )
    ) {
      return;
    }
    onOffre(offreDepuisNotes(brouillon));
    setBrouillon(null);
  };

  const enCours = etat === EN_COURS;

  if (brouillon) {
    const aSaisir = brouillon.lignes.filter(sansPrix).length;
    return (
      <div
        key="brouillon"
        role="region"
        aria-label="Vérifier le brouillon d'offre"
        style={{ border: '1.5px solid ' + C.purple, borderRadius: 11, padding: 12 }}
      >
        <div style={{ fontSize: 13, fontWeight: 800, color: C.purple, marginBottom: 4 }}>
          🤖 Brouillon d'offre — à vérifier
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
          {brouillon.lignes.length} ligne(s) · {brouillon.lignes.length - aSaisir} prix du catalogue
          · {aSaisir} à compléter
        </div>
        <input
          aria-label="Objet de l'offre"
          value={brouillon.titre}
          onChange={(e) => setBrouillon((b) => ({ ...b, titre: e.target.value }))}
          placeholder="Objet de l'offre"
          style={{ ..._inp, marginBottom: 8 }}
        />
        <div style={{ fontSize: 11, fontWeight: 700, color: C.grey, marginBottom: 4 }}>Client</div>
        <select
          aria-label="Client de l'offre"
          value={brouillon.choixClient}
          onChange={(e) => setBrouillon((b) => ({ ...b, choixClient: e.target.value }))}
          style={{ ..._inp, marginBottom: 7 }}
        >
          {brouillon.candidats.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {'✓ ' + c.nom + (c.npa ? ' — ' + c.npa : '')}
            </option>
          ))}
          <option value={NOUVEAU_CLIENT}>
            {'＋ Créer le client' +
              (brouillon.nouveauClient.nom ? ' « ' + brouillon.nouveauClient.nom + ' »' : '')}
          </option>
          <option value="">Sans client pour l'instant</option>
        </select>
        {brouillon.candidats.length > 1 && (
          <div style={{ fontSize: 10.5, color: C.amber, marginBottom: 7 }}>
            Plusieurs clients portent ce nom : choisis le bon.
          </div>
        )}
        {brouillon.choixClient === NOUVEAU_CLIENT && (
          <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
            {[
              ['nom', 'Nom du client *'],
              ['adr', 'Adresse'],
              ['npa', 'NPA / Ville'],
              ['tel', 'Téléphone'],
              ['email', 'E-mail'],
            ].map(([champ, libelle]) => (
              <input
                key={champ}
                aria-label={libelle}
                value={brouillon.nouveauClient[champ]}
                onChange={(e) => majClient(champ, e.target.value)}
                placeholder={libelle}
                style={_inp}
              />
            ))}
          </div>
        )}
        {brouillon.corrections.length > 0 && (
          <div style={{ fontSize: 10.5, color: C.muted, margin: '4px 0 8px', lineHeight: 1.5 }}>
            Corrigé : {brouillon.corrections.join(' · ')}
          </div>
        )}
        {brouillon.lignes.map((l) => (
          <div
            key={l.id}
            style={{
              border: '1px solid ' + (sansPrix(l) ? C.amber : C.border),
              borderRadius: 9,
              padding: 8,
              marginBottom: 7,
            }}
          >
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input
                aria-label="Désignation"
                value={l.d}
                onChange={(e) => majLigne(l.id, 'd', e.target.value)}
                style={{ ..._inp, flex: 1 }}
              />
              <button
                type="button"
                aria-label={'Retirer ' + l.d}
                onClick={() =>
                  setBrouillon((b) => ({ ...b, lignes: b.lignes.filter((x) => x.id !== l.id) }))
                }
                style={{ border: 'none', background: 'none', color: C.red, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                aria-label="Quantité"
                inputMode="decimal"
                value={l.q}
                onChange={(e) => majLigne(l.id, 'q', e.target.value.replace(',', '.'))}
                placeholder="Qté"
                style={{ ..._inp, flex: 1 }}
              />
              <input
                aria-label="Unité"
                value={l.u}
                onChange={(e) => majLigne(l.id, 'u', e.target.value)}
                placeholder="Unité"
                style={{ ..._inp, flex: 1 }}
              />
              <input
                aria-label={'Prix ' + DEVISE}
                inputMode="decimal"
                value={l.p}
                onChange={(e) => majLigne(l.id, 'p', e.target.value.replace(',', '.'))}
                placeholder="Prix à compléter"
                style={{ ..._inp, flex: 1.4 }}
              />
            </div>
            <div style={{ fontSize: 10, marginTop: 5, color: l.nouveau ? C.amber : C.green }}>
              {l.nouveau
                ? '＋ Article absent du catalogue — prix à compléter'
                : '✓ Catalogue : ' + l.catalogue}
            </div>
            {l.alertes
              .filter((a) => !a.startsWith('Absent du catalogue'))
              .map((a) => (
                <div key={a} style={{ fontSize: 10, color: C.amber, marginTop: 2 }}>
                  ⚠ {a}
                </div>
              ))}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 7, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <Btn
              label="✓ Créer l'offre"
              color={C.green}
              full
              disabled={!brouillon.lignes.length}
              onClick={cree}
            />
          </div>
          <Btn label="Annuler" ghost color={C.grey} onClick={() => setBrouillon(null)} />
        </div>
      </div>
    );
  }

  return (
    <div key="bouton">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 8px' }}>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: C.muted,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          ou, en un scan
        </span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
      </div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.purple,
          textAlign: 'center',
          marginBottom: 6,
        }}
      >
        🔒 Nécessite le Pack IA (+9.–/mois)
      </div>
      <button
        type="button"
        disabled={enCours}
        onClick={() => entree.current && entree.current.click()}
        style={{
          width: '100%',
          background: enCours ? C.border : 'linear-gradient(135deg,#7c3aed,#6025c9)',
          color: '#fff',
          border: 'none',
          borderRadius: 11,
          padding: 14,
          fontWeight: 800,
          fontSize: 14,
          cursor: enCours ? 'wait' : 'pointer',
          marginBottom: 12,
          boxShadow: '0 3px 12px rgba(124,58,237,.25)',
        }}
      >
        {enCours ? '🤖 Lecture de la feuille…' : "🤖 Faire une offre avec l'IA"}
        <div style={{ fontSize: 9, opacity: 0.9, fontWeight: 600, marginTop: 2 }}>
          Scanne ta feuille → offre auto · Pack IA
        </div>
      </button>
      <input
        ref={entree}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,application/pdf"
        aria-label="Feuille de notes à lire"
        style={{ display: 'none' }}
        onChange={(e) => {
          const fichiers = Array.from(e.target.files || []);
          e.target.value = '';
          if (fichiers.length) lit(fichiers);
        }}
      />
      {etat && !enCours && (
        <div role="alert" style={{ fontSize: 10.5, color: C.red, marginTop: 6, lineHeight: 1.5 }}>
          {etat}
        </div>
      )}
    </div>
  );
}
